import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
	Alert,
	Box,
	Chip,
	Grid,
	Paper,
	Stack,
	Typography
} from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import { keyframes } from '@mui/system';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import { useActiveTickets } from './useActiveTickets';
import { WS_STATUS } from '../../common/hooks/useWebSocket';
import { LoadingContainer } from '../../common/helpers/LoadingContainer';
import './activeTickets.scss';

const STATUS_CONFIG = {
	[WS_STATUS.CONNECTING]:  { label: 'Подключение…',      dotColor: '#9e9e9e', alertSeverity: null },
	[WS_STATUS.CONNECTED]:   { label: 'В реальном времени', dotColor: '#4caf50', alertSeverity: null },
	[WS_STATUS.RECONNECTING]:{ label: 'Переподключение…',   dotColor: '#ff9800', alertSeverity: 'warning', alertText: 'Связь прервана. Данные обновляются раз в 30 сек.' },
	[WS_STATUS.DISCONNECTED]:{ label: 'Нет связи',          dotColor: '#f44336', alertSeverity: 'error',   alertText: 'Нет подключения к серверу. Данные могут быть неактуальны.' }
};

const MAX_WAITING_SHOWN = 10;

function ActiveTickets() {
	const { tickets, loading, errorMessage, wsStatus } = useActiveTickets();
	const statusCfg = STATUS_CONFIG[wsStatus] ?? STATUS_CONFIG[WS_STATUS.CONNECTING];

	const [callOrder, setCallOrder]     = useState({});
	const callCounter                   = useRef(0);
	const [highlighted, setHighlighted] = useState({});
	const prevStatusRef                 = useRef(new Map());
	const timersRef                     = useRef(new Map());

	useEffect(() => {
		const prevMap = prevStatusRef.current;
		const nextMap = new Map();
		(tickets || []).forEach(t => {
			const id         = t.ticketNumber;
			const nextStatus = String(t.ticketStatus || '').toUpperCase();
			const prevStatus = prevMap.get(id);
			if (nextStatus === 'CALLED' && prevStatus !== 'CALLED') {
				callCounter.current += 1;
				setCallOrder(o => ({ ...o, [id]: callCounter.current }));
				setHighlighted(h => ({ ...h, [id]: true }));
				if (timersRef.current.has(id)) clearTimeout(timersRef.current.get(id));
				timersRef.current.set(id, setTimeout(() => {
					setHighlighted(h => { const c = { ...h }; delete c[id]; return c; });
					timersRef.current.delete(id);
				}, 8000));
			}
			if (nextStatus === 'DONE') {
				if (timersRef.current.has(id)) { clearTimeout(timersRef.current.get(id)); timersRef.current.delete(id); }
				setHighlighted(h => { if (!h[id]) return h; const c = { ...h }; delete c[id]; return c; });
				setCallOrder(o => { const c = { ...o }; delete c[id]; return c; });
			}
			nextMap.set(id, nextStatus);
		});
		prevStatusRef.current = nextMap;
	}, [tickets]);

	useEffect(() => () => {
		timersRef.current.forEach(t => clearTimeout(t));
		timersRef.current.clear();
	}, []);

	const calledTickets = useMemo(
		() => (tickets || [])
			.filter(t => String(t.ticketStatus).toUpperCase() === 'CALLED')
			.sort((a, b) => (callOrder[b.ticketNumber] ?? 0) - (callOrder[a.ticketNumber] ?? 0)),
		[tickets, callOrder]
	);
	const allWaiting  = useMemo(
		() => (tickets || []).filter(t => String(t.ticketStatus).toUpperCase() === 'WAITING'),
		[tickets]
	);
	const waitingShown = allWaiting.slice(0, MAX_WAITING_SHOWN);
	const waitingExtra = allWaiting.length - waitingShown.length;

	if (loading) return <LoadingContainer />;

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
			{errorMessage && (
				<Alert severity='error' sx={{ mb: '1rem', fontSize: '0.85rem' }}>{errorMessage}</Alert>
			)}
			{statusCfg.alertSeverity && (
				<Alert severity={statusCfg.alertSeverity} sx={{ mb: '1rem', fontSize: '0.85rem' }}>
					{statusCfg.alertText}
				</Alert>
			)}

			{/* Заголовок */}
			<Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mb: '1.5rem' }}>
				<Typography sx={{ fontWeight: 900, fontSize: '1.75rem', letterSpacing: -0.5 }}>
					Табло очереди
				</Typography>
				<Stack direction='row' alignItems='center' spacing='0.5rem'>
					<Box sx={{
						width: '0.55rem', height: '0.55rem',
						borderRadius: '50%',
						bgcolor: statusCfg.dotColor,
						flexShrink: 0,
						...(wsStatus === WS_STATUS.RECONNECTING && { animation: 'ws-pulse 1.4s ease-in-out infinite' })
					}} />
					<Typography sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
						{statusCfg.label}
					</Typography>
				</Stack>
			</Stack>

			{/* Пустое состояние */}
			{calledTickets.length === 0 && allWaiting.length === 0 && (
				<Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
					<Typography sx={{ fontWeight: 700, fontSize: '2.2rem', color: 'text.secondary', textAlign: 'center' }}>
						Очередь пуста
					</Typography>
					<Typography sx={{ color: 'text.disabled', mt: '1rem', fontSize: '1.1rem', textAlign: 'center' }}>
						Ожидаем первого посетителя
					</Typography>
				</Box>
			)}

			{/* СЕЙЧАС ОБСЛУЖИВАЕТСЯ */}
			{calledTickets.length > 0 && (
				<Box sx={{ mb: '2rem' }}>
					<SectionBadge $color='success'>
						<NotificationsActiveRoundedIcon sx={{ fontSize: '1rem' }} />
						<span>СЕЙЧАС ОБСЛУЖИВАЕТСЯ</span>
						<Chip
							label={calledTickets.length}
							size='small'
							sx={{ height: '1.3rem', fontSize: '0.7rem', fontWeight: 700, bgcolor: 'rgba(255,255,255,.25)', color: '#fff' }}
						/>
					</SectionBadge>

					<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '0.9rem', mt: '1rem' }}>
						{calledTickets.map(t => (
							<CalledCard key={t.ticketNumber} $isNew={!!highlighted[t.ticketNumber]} elevation={0}>
								{t.serviceType && (
									<Chip size='small' label={t.serviceType.nameRu}
										sx={{ backgroundColor: t.serviceType.color || '#EDE9FE', fontWeight: 700, fontSize: '0.75rem', mb: '0.5rem', height: '1.5rem' }}
									/>
								)}
								<Stack direction='row' alignItems='center' justifyContent='space-between'>
									<BigNumber>{t.ticketNumber}</BigNumber>
									<ArrowForwardRoundedIcon sx={{ fontSize: '2.5rem', opacity: 0.35, mx: '0.5rem' }} />
									<Stack alignItems='flex-end'>
										<Typography sx={{ fontSize: '0.65rem', fontWeight: 600, opacity: 0.5, mb: '0.2rem' }}>
											ОКНО
										</Typography>
										<BigNumber>{t.windowNumber || '—'}</BigNumber>
									</Stack>
								</Stack>
							</CalledCard>
						))}
					</Box>
				</Box>
			)}

			{/* В ОЧЕРЕДИ */}
			{allWaiting.length > 0 && (
				<Box>
					<SectionBadge $color='primary' style={{ marginBottom: '1rem' }}>
						<AccessTimeRoundedIcon sx={{ fontSize: '0.9rem' }} />
						<span>В ОЧЕРЕДИ</span>
						<Chip
							label={allWaiting.length}
							size='small'
							sx={{ height: '1.3rem', fontSize: '0.7rem', fontWeight: 700, bgcolor: 'rgba(25,118,210,.12)', color: 'primary.main' }}
						/>
					</SectionBadge>

					<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem' }}>
						{waitingShown.map(t => (
							<WaitingChip key={t.ticketNumber}>
								<Typography sx={{ fontWeight: 800, fontSize: '1.5rem', lineHeight: 1 }}>
									{t.ticketNumber}
								</Typography>
								{t.serviceType && (
									<Typography sx={{ fontSize: '0.65rem', opacity: 0.6, mt: '0.2rem', lineHeight: 1 }}>
										{t.serviceType.nameRu}
									</Typography>
								)}
							</WaitingChip>
						))}
						{waitingExtra > 0 && (
							<WaitingChip $muted>
								<Typography sx={{ fontWeight: 700, fontSize: '1.1rem', opacity: 0.5 }}>
									+{waitingExtra}
								</Typography>
								<Typography sx={{ fontSize: '0.65rem', opacity: 0.4, mt: '0.2rem' }}>
									ещё
								</Typography>
							</WaitingChip>
						)}
					</Box>
				</Box>
			)}

			<style>{`
				@keyframes ws-pulse { 0%,100%{opacity:1} 50%{opacity:.3} }
			`}</style>
		</Box>
	);
}

// ─── Styled components ────────────────────────────────────────────────────────

const pulseGreen = (theme) => keyframes`
	0%   { box-shadow: 0 0 0 0 ${alpha(theme.palette.success.main, 0.55)}; }
	70%  { box-shadow: 0 0 0 0.9rem rgba(0,0,0,0); }
	100% { box-shadow: 0 0 0 0 rgba(0,0,0,0); }
`;

const SectionBadge = styled('div', {
	shouldForwardProp: p => p !== '$color'
})(({ theme, $color }) => {
	const isSuccess = $color === 'success';
	return {
		display:         'inline-flex',
		alignItems:      'center',
		gap:             '0.4rem',
		padding:         '0.3rem 0.9rem',
		borderRadius:    '2rem',
		fontWeight:      800,
		fontSize:        '0.7rem',
		letterSpacing:   1.5,
		backgroundColor: isSuccess ? alpha(theme.palette.success.main, 0.88) : alpha(theme.palette.primary.main, 0.09),
		color:           isSuccess ? '#fff' : theme.palette.primary.main,
	};
});

const CalledCard = styled(Paper, {
	shouldForwardProp: p => p !== '$isNew'
})(({ theme, $isNew }) => ({
	flex:            '1 1 calc(50% - 0.45rem)',
	minWidth:        '12rem',
	padding:         '1.2rem 1.5rem',
	borderRadius:    '1rem',
	border:          `2px solid ${alpha(theme.palette.success.main, $isNew ? 0.7 : 0.22)}`,
	backgroundColor: $isNew ? alpha(theme.palette.success.main, 0.07) : alpha(theme.palette.success.main, 0.03),
	transition:      'background-color 400ms ease, border-color 400ms ease',
	...($isNew && { animation: `${pulseGreen(theme)} 1.8s ease-out infinite` }),
}));

const BigNumber = styled(Typography)(({ theme }) => ({
	fontWeight:         900,
	lineHeight:         1,
	fontSize:           '4.5rem',
	fontVariantNumeric: 'tabular-nums',
	letterSpacing:      '-0.02em',
	color:              theme.palette.success.dark,
}));

const WaitingChip = styled(Box, {
	shouldForwardProp: p => p !== '$muted'
})(({ theme, $muted }) => ({
	display:         'flex',
	flexDirection:   'column',
	alignItems:      'center',
	justifyContent:  'center',
	padding:         '0.6rem 0.9rem',
	borderRadius:    '0.75rem',
	minWidth:        '4rem',
	border:          `1.5px solid ${alpha(theme.palette.primary.main, $muted ? 0.1 : 0.18)}`,
	backgroundColor: $muted ? alpha(theme.palette.divider, 0.3) : alpha(theme.palette.primary.main, 0.05),
	color:           theme.palette.primary.main,
}));

export default ActiveTickets;
